import { useEffect, useRef, useState } from 'react';
import { OrderView, toOrderView } from './adapters';
import { OpsContext } from './useOpsContext';
import { useRetractOpsOrderMutation } from '../../services/useOpsBriefingService';
import { describeRetractDelivery, notifiesCoachOnRetract } from './retractDelivery';

/**
 * 지시 철회 — 확인 단계.
 *
 * ═══════════════════════════════════════════════════════════
 * 왜 확인 창을 두는가
 * ═══════════════════════════════════════════════════════════
 * 철회는 **종단**입니다. 되돌리는 버튼이 없고, 다시 시키려면 새 지시를 내야
 * 합니다. 그리고 강사가 이미 본 지시라면 **강사에게 알림이 나갑니다** —
 * 목록에서 한 줄 지우는 일이 아니라 사람에게 말을 거는 일입니다.
 *
 * 그래서 지시를 보낼 때와 같은 문법을 씁니다(OrderConfirmModal). 이 창도
 * "귀찮은 관문"이 아니라 **마지막 눈**입니다.
 *   · 무엇을 철회하는지 요약 없이 그대로 보여주고
 *   · 이번 철회가 **회수인지 통보인지**를 먼저 말해 주고
 *   · 회수면 원장을 멈춰 세우지 않고 (Enter 한 번에 끝)
 *   · 통보면 왜 사유가 필요한지 적고 나서 받습니다
 *   · 실패하면 창을 닫지 않습니다 — 닫히면 철회됐는지 알 방법이 없습니다
 *
 * ⚠️ 사유가 필요한지는 **서버가** 정합니다. 이 창의 판정은 마지막 조회 시점의
 *    `seenAt` 을 본 추정이라, 그 사이에 강사가 열어 보면 400 이 옵니다.
 *    그때는 창을 닫지 않고 사유를 받아 다시 묻습니다.
 */

const BORDER = '#E3EAF3';
/** 철회는 되돌릴 수 없는 동작이라 보내기(네이비)와 색을 나눈다 */
const DEEP_RED = '#8C1D24';

/** 서버 상한(Joi max 500). 여기만 넓히면 400 이 난다 */
export const RETRACT_REASON_MAX = 500;

type ModalProps = {
    order: OrderView;
    /**
     * 사유를 반드시 받아야 하는가.
     *
     * 화면 추정(seenAt)과 서버가 400 으로 알려준 사실을 **합쳐서** 넘깁니다.
     * 한 번 400 을 받은 뒤에는 화면 추정이 뭐라 하든 필수입니다.
     */
    reasonRequired: boolean;
    sending: boolean;
    /** 실패했을 때 창을 닫지 않고 여기에 적는다 */
    error: string | null;
    onCancel: () => void;
    onConfirm: (reason: string) => void;
};

export const OrderRetractModal = ({
    order,
    reasonRequired,
    sending,
    error,
    onCancel,
    onConfirm,
}: ModalProps) => {
    const [reason, setReason] = useState('');
    const confirmRef = useRef<HTMLButtonElement>(null);
    const reasonRef = useRef<HTMLTextAreaElement>(null);
    const coach = order.coachName ? `${order.coachName} 강사` : '담당 강사';

    /*
     * 포커스를 어디에 두는가 — 갈래마다 다릅니다.
     *
     *   회수  주 버튼. 지시를 보낼 때와 똑같이 **Enter 한 번**으로 끝납니다.
     *         읽을 사람이 없는 글을 쓰느라 원장을 멈춰 세우지 않습니다.
     *   통보  사유 칸. 여기서 주 버튼에 포커스를 주면 Enter 를 눌러도 아무 일이
     *         일어나지 않습니다(사유가 비어 버튼이 잠겨 있으므로). 원장은 창이
     *         고장 난 줄 압니다.
     *
     * 통보 갈래의 Enter 는 줄바꿈이어야 합니다 — 사유는 여러 줄일 수 있습니다.
     * 대신 ⌘/Ctrl+Enter 로 확정할 수 있게 열어 둡니다.
     */
    useEffect(() => {
        if (reasonRequired) reasonRef.current?.focus();
        else confirmRef.current?.focus();
    }, [reasonRequired]);

    /* Esc 로 닫는다 (보내는 중에는 막는다) — 보내기 확인 창과 같은 규칙 */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !sending) onCancel();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onCancel, sending]);

    const missingReason = reasonRequired && !reason.trim();
    const blocked = sending || missingReason;
    const confirm = () => {
        if (blocked) return;
        onConfirm(reason);
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ background: 'rgba(12,22,40,0.55)', zIndex: 2500, padding: 24 }}
            onClick={() => {
                if (!sending) onCancel();
            }}
            role="presentation"
        >
            <div
                className="flex flex-col rounded-[18px]"
                style={{
                    width: 560,
                    maxHeight: '88vh',
                    background: '#FFFFFF',
                    boxShadow: '0 30px 80px rgba(12,22,40,0.35)',
                    overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="지시 철회 확인"
            >
                <div style={{ background: DEEP_RED, padding: '20px 26px' }}>
                    <span style={{ fontSize: 19, fontWeight: 800, color: '#FFFFFF' }}>
                        이 지시를 철회할까요?
                    </span>
                    <div style={{ marginTop: 5 }}>
                        <span style={{ fontSize: 14, color: '#F3CFD1' }}>
                            <b style={{ color: '#FFFFFF' }}>{coach}</b>에게 보낸{' '}
                            <b style={{ color: '#FFFFFF' }}>{order.memberName} 회원</b> 건입니다
                        </span>
                    </div>
                </div>

                <div style={{ padding: '20px 26px', overflowY: 'auto' }}>
                    {/* 무엇을 철회하는가 — 요약하지 않고 보낸 문장 그대로 */}
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#5B6B7F' }}>
                        철회할 지시
                    </span>
                    <div
                        className="rounded-[9px]"
                        style={{
                            marginTop: 7,
                            background: '#F7F9FC',
                            border: `1px solid ${BORDER}`,
                            borderLeft: `3px solid ${DEEP_RED}`,
                            padding: '13px 15px',
                            whiteSpace: 'pre-line',
                        }}
                    >
                        <span style={{ fontSize: 15.5, color: '#16202E', lineHeight: '24px' }}>
                            {order.task}
                        </span>
                        <div style={{ marginTop: 6 }}>
                            <span style={{ fontSize: 13, color: '#93A0AE' }}>
                                {order.sentLabel} 기록
                            </span>
                        </div>
                    </div>

                    {/*
                      갈래를 **먼저** 말한다.

                      원장이 알아야 하는 것은 "철회하면 어떻게 되나" 입니다.
                      강사가 아무것도 못 보고 지나가는지, 강사에게 말이 가는지가
                      완전히 다른 일이고, 사유를 요구하는 이유도 여기서 나옵니다.
                    */}
                    {reasonRequired ? (
                        <div
                            className="rounded-[10px]"
                            style={{
                                marginTop: 16,
                                border: '1px solid #F5D08A',
                                background: '#FFFAF0',
                                padding: '12px 14px',
                            }}
                        >
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#B45309' }}>
                                {coach}가 이미 이 지시를 봤습니다
                                {order.seenLabel ? ` · ${order.seenLabel} 확인` : ''}
                            </span>
                            <div style={{ marginTop: 5 }}>
                                <span
                                    style={{ fontSize: 13.5, color: '#8A6A2F', lineHeight: '20px' }}
                                >
                                    회원에게 이미 연락했을 수도 있습니다. 철회하면 강사에게 알림이
                                    갈 수 있으니 왜 취소하는지 함께 알려주세요.
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div
                            className="rounded-[10px]"
                            style={{
                                marginTop: 16,
                                border: `1px solid ${BORDER}`,
                                background: '#F7F9FC',
                                padding: '12px 14px',
                            }}
                        >
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#425063' }}>
                                {coach}가 아직 열어 보지 않았습니다
                            </span>
                            <div style={{ marginTop: 5 }}>
                                <span
                                    style={{ fontSize: 13.5, color: '#5B6B7F', lineHeight: '20px' }}
                                >
                                    조용히 거둬들입니다. 강사 앱에서 내려가고 알림은 가지 않습니다 —
                                    본 적 없는 지시를 "취소됐습니다"라고 알릴 이유가 없습니다.
                                </span>
                            </div>
                        </div>
                    )}

                    {/*
                      완료 보고가 있는 건을 철회하는 경우.
                      "강사가 한 일까지 지워지나" 는 원장이 당연히 걱정할 지점입니다.
                      서버는 doneAt·doneBy·coachReport 를 건드리지 않습니다.
                    */}
                    {order.status === 'DONE' && (
                        <div style={{ marginTop: 10 }}>
                            <span style={{ fontSize: 13, color: '#5B6B7F', lineHeight: '19px' }}>
                                이미 완료 보고된 건입니다. 강사가 한 일과 보고 내용은 그대로
                                남습니다.
                            </span>
                        </div>
                    )}

                    {/* 사유 — 통보 갈래에서만 필수. 회수 갈래에서도 적을 수는 있다 */}
                    <div className="flex items-baseline" style={{ marginTop: 18, gap: 7 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#5B6B7F' }}>
                            철회 사유
                        </span>
                        <span
                            className="rounded-[5px]"
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                color: reasonRequired ? '#B45309' : '#5B6B7F',
                                background: reasonRequired ? '#FDF1DC' : '#F1F4F9',
                                padding: '2px 7px',
                            }}
                        >
                            {reasonRequired ? '강사에게 함께 갑니다 · 필수' : '선택'}
                        </span>
                    </div>
                    <textarea
                        ref={reasonRef}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        onKeyDown={(e) => {
                            /*
                             * 사유 칸에서 Enter 는 줄바꿈입니다. 확정은 ⌘/Ctrl+Enter —
                             * 보내기 확인 창의 "Enter 한 번" 을 이 갈래에서도 흉내내되,
                             * 여러 줄로 적을 자유를 뺏지 않습니다.
                             */
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                e.preventDefault();
                                confirm();
                            }
                        }}
                        rows={3}
                        maxLength={RETRACT_REASON_MAX}
                        aria-label="철회 사유"
                        placeholder={
                            reasonRequired
                                ? '예: 이미 어제 제가 직접 연락드린 회원이라 안 하셔도 됩니다.'
                                : '적지 않으셔도 됩니다.'
                        }
                        style={{
                            marginTop: 7,
                            width: '100%',
                            background: '#FFFFFF',
                            border: `1px solid ${reasonRequired ? '#E8B87A' : '#DCE4EE'}`,
                            borderRadius: 9,
                            padding: '12px 14px',
                            fontSize: 14.5,
                            color: '#3A4757',
                            lineHeight: '22px',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            outline: 'none',
                        }}
                    />
                    <div style={{ marginTop: 5 }}>
                        <span style={{ fontSize: 12.5, color: '#93A0AE', lineHeight: '18px' }}>
                            {reasonRequired
                                ? `철회 알림이 나가면 ${coach}가 이 글을 함께 읽습니다. 이유 없는 철회는 잔소리로 읽힙니다.`
                                : '읽을 사람이 없어 선택입니다. 적어 두시면 기록으로만 남습니다.'}
                        </span>
                    </div>

                    {/* 되돌릴 수 없다는 사실 — 확정하는 자리에 있어야 한다 */}
                    <div style={{ marginTop: 14 }}>
                        <span style={{ fontSize: 13, color: '#5B6B7F', lineHeight: '19px' }}>
                            철회는 되돌릴 수 없습니다. 다시 시키시려면 새 지시를 내야 합니다.
                        </span>
                    </div>

                    {/* 실패는 창 안에서 말한다. 창이 닫히면 철회됐는지 알 수 없다 */}
                    {error && (
                        <div
                            className="rounded-[10px]"
                            style={{
                                marginTop: 14,
                                border: '1px solid #FBD0D3',
                                background: '#FFE8E9',
                                padding: '12px 14px',
                            }}
                            role="alert"
                        >
                            <span style={{ fontSize: 13.5, color: '#C22D38', lineHeight: '20px' }}>
                                {error}
                            </span>
                        </div>
                    )}
                </div>

                <div
                    className="flex items-center"
                    style={{
                        borderTop: `1px solid ${BORDER}`,
                        background: '#FBFCFE',
                        padding: '14px 26px',
                        gap: 10,
                    }}
                >
                    <span style={{ fontSize: 12.5, color: '#93A0AE' }}>
                        Esc 를 누르면 그대로 둡니다
                    </span>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={sending}
                        className="rounded-[10px]"
                        style={{
                            marginLeft: 'auto',
                            height: 46,
                            padding: '0 22px',
                            background: '#F1F4F9',
                            border: 'none',
                            color: '#425063',
                            fontSize: 15,
                            fontWeight: 700,
                            cursor: sending ? 'not-allowed' : 'pointer',
                        }}
                    >
                        그대로 두기
                    </button>
                    <button
                        ref={confirmRef}
                        type="button"
                        onClick={confirm}
                        /*
                         * 키를 누르고 있으면 브라우저가 keydown 을 반복해서 보낸다.
                         * 목록의 철회 버튼을 Enter 로 누른 채 손을 떼지 않으면 방금 열린
                         * 이 창의 확정 버튼까지 눌린다 — 원장은 아무것도 읽지 못한 채
                         * 되돌릴 수 없는 동작을 지나간다.
                         */
                        onKeyDown={(e) => {
                            if (e.repeat) e.preventDefault();
                        }}
                        /*
                         * 연타 방지의 1차 방어(눈에 보이는 잠금).
                         * 실제 잠금은 useOrderRetract 의 ref 가드다 — isLoading 은
                         * 다음 렌더에나 켜지기 때문이다.
                         */
                        disabled={blocked}
                        title={missingReason ? '철회 사유를 적어 주세요' : undefined}
                        className="rounded-[10px]"
                        style={{
                            height: 46,
                            padding: '0 24px',
                            background: blocked ? '#C0A7A9' : DEEP_RED,
                            border: 'none',
                            color: '#FFFFFF',
                            fontSize: 15.5,
                            fontWeight: 700,
                            cursor: sending ? 'progress' : missingReason ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {sending ? '철회하는 중…' : error ? '다시 철회하기' : '지시 철회하기'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════
   흐름
   ═══════════════════════════════════════════════════════════ */

const getStatus = (error: unknown): number | undefined => {
    const status = (error as { response?: { status?: number } } | null | undefined)?.response
        ?.status;
    return typeof status === 'number' ? status : undefined;
};

/**
 * 실패 문구.
 *
 * 400 만 특별합니다 — 그건 "못 했다"가 아니라 **"사유를 주면 된다"** 입니다.
 * 서버 메시지를 그대로 쓰지 않고 화면 말로 바꿉니다. 원장이 방금 본 화면에는
 * "강사가 아직 안 봤습니다"라고 적혀 있었으므로, 무엇이 달라졌는지를 말해야
 * 원장이 납득하고 사유를 적습니다.
 */
const failMessage = (error: unknown): string => {
    const status = getStatus(error);
    if (status === 400)
        return '방금 사이에 강사가 이 지시를 확인했습니다. 이제 철회하면 강사에게 알림이 갑니다 — 왜 취소하는지 적어 주세요.';
    if (status === 409) return '이미 철회된 지시입니다. 창을 닫으시면 목록에 반영됩니다.';
    if (status === 404) return '이미 정리됐거나 찾을 수 없는 지시입니다. 창을 닫으시면 목록에 반영됩니다.';
    /*
     * ⚠️ 여기서 "철회하지 못했습니다"라고 단정하면 안 됩니다.
     *
     * 서버가 철회를 끝낸 뒤 응답만 유실돼도 화면에는 똑같은 네트워크 오류가
     * 옵니다. 그때 "못 했습니다"라고 말하면 원장은 다시 누르고 409 를 받습니다.
     * 우리가 아는 것은 **답을 못 받았다**까지입니다.
     */
    const msg = (error as { response?: { data?: { message?: string } } } | null)?.response?.data
        ?.message;
    if (status === undefined)
        return '철회 결과를 확인하지 못했습니다. 창을 닫고 목록에서 철회됐는지 확인해 주세요.';
    return msg || '철회하지 못했습니다. 잠시 후 다시 시도해 주세요.';
};

type Options = {
    /** 결과를 화면에 알릴 방법 (진행 현황의 토스트) */
    notify: (message: string) => void;
};

export const useOrderRetract = (ctx: OpsContext, { notify }: Options) => {
    const [target, setTarget] = useState<OrderView | null>(null);
    const [error, setError] = useState<string | null>(null);
    /**
     * 서버가 400 으로 "사유가 필요하다"고 알려 준 적이 있는가.
     *
     * 화면 추정(seenAt)이 틀렸다는 뜻이므로, 한 번 켜지면 이 창이 닫힐 때까지
     * 유지합니다. 다시 화면 추정으로 되돌아가면 사유 칸이 '선택'으로 바뀌고
     * 원장은 같은 400 을 또 만납니다.
     */
    const [reasonRequiredByServer, setReasonRequiredByServer] = useState(false);
    const retract = useRetractOpsOrderMutation(ctx);

    /*
     * 확정이 두 번 눌리는 것을 막는다.
     * mutation.isLoading 만으로는 부족하다 — 같은 렌더 안에서 두 번 누르면
     * 아직 false 라 두 번 나간다. 두 번째는 409 로 떨어지겠지만, 원장 화면에는
     * "이미 철회된 지시입니다"라는 뜬금없는 실패가 뜬다.
     */
    const sendingRef = useRef(false);

    const ask = (order: OrderView) => {
        if (sendingRef.current) return;
        setError(null);
        setReasonRequiredByServer(false);
        setTarget(order);
    };

    const cancel = () => {
        if (sendingRef.current) return;
        setError(null);
        setTarget(null);
    };

    const confirm = (reason: string) => {
        if (!target) return;
        if (sendingRef.current) return;
        sendingRef.current = true;
        setError(null);

        const trimmed = reason.trim();
        retract.mutate(
            {
                orderId: target.orderId,
                /*
                 * 빈 사유는 **키 자체를 보내지 않습니다.**
                 * 서버 Joi 는 ''(빈 문자열)도 받지만, 그러면 "사유를 안 적었다"와
                 * "빈 글을 적었다"가 서버에서 같아집니다. 회수 갈래에서는 어차피
                 * 같지만 통보 갈래의 400 판정은 이 값을 봅니다.
                 */
                body: trimmed ? { reason: trimmed } : {},
            },
            {
                onSuccess: (updated) => {
                    sendingRef.current = false;
                    setTarget(null);
                    /*
                     * 토스트 문구를 화면이 지어내지 않습니다. **서버가 돌려준 결과**로
                     * 만듭니다 — 회수였는지 통보였는지, 통보였다면 닿았는지는
                     * 응답에만 있습니다. 눌렀으니 갔겠지 하고 "전달했습니다"라고
                     * 쓰면, 그게 정확히 이 기능이 막으려던 거짓말입니다.
                     */
                    const view = describeRetractDelivery(toOrderView(updated));
                    notify(`철회했습니다 · ${view ? view.title : '아래 철회 목록에 남았습니다'}`);
                },
                onError: (e) => {
                    sendingRef.current = false;
                    if (getStatus(e) === 400) setReasonRequiredByServer(true);
                    // 실패해도 창을 닫지 않는다. 닫으면 철회됐는지 알 방법이 없다.
                    setError(failMessage(e));
                },
            }
        );
    };

    const modal = target ? (
        <OrderRetractModal
            key={target.orderId}
            order={target}
            reasonRequired={reasonRequiredByServer || notifiesCoachOnRetract(target)}
            sending={retract.isLoading}
            error={error}
            onCancel={cancel}
            onConfirm={confirm}
        />
    ) : null;

    return {
        /** 확인 창 열기 — 철회는 여기를 지나야만 나간다 */
        ask,
        /** 화면 맨 아래에 그대로 놓으면 된다 */
        modal,
        /** 서버 왕복 중인가 — 목록 버튼을 함께 잠글 때 쓴다 */
        sending: retract.isLoading,
    };
};

export default OrderRetractModal;
